import { NavLink, Outlet } from 'react-router-dom'
import { Bug, Lightbulb, ListChecks, SignOut, Star } from '@phosphor-icons/react'
import { BrandWordmark } from '@/components/ui/Logomark'
import { cn } from '@/lib/utils'
import { sectionEyebrowClass, sectionEyebrowStyle } from '@/lib/styleKit'
import { useAdminAuthStore } from '../adminAuthStore'

/**
 * Panel chrome (ADMIN_FLOW.md §8.1): slim top bar + left rail with the
 * Module 1 tabs. Module 2 (Users & Analytics) is deliberately not rendered;
 * the NAV array is where it will slot in.
 */
const NAV = [
  { to: '/admin/status', label: 'Status', icon: ListChecks },
  { to: '/admin/bugs', label: 'Bugs', icon: Bug },
  { to: '/admin/suggestions', label: 'Suggestions', icon: Lightbulb },
  { to: '/admin/ratings', label: 'Ratings', icon: Star },
]

const navText = { fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' } as const

export function AdminShell() {
  const admin = useAdminAuthStore((s) => s.admin)
  const logout = useAdminAuthStore((s) => s.logout)

  return (
    <div className="min-h-screen bg-background text-foreground dark:bg-[#0f1011] dark:text-[#f7f8f8] flex flex-col">
      <header className="h-12 border-b border-border/50 dark:border-white/[0.06] flex items-center justify-between px-4 sm:px-5">
        <div className="flex items-center gap-2.5">
          <BrandWordmark height={20} />
          <span
            className="rounded-md bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] px-1.5 h-5 inline-flex items-center"
            style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '0.04em' }}
          >
            ADMIN
          </span>
        </div>
        <div className="flex items-center gap-3">
          {admin && (
            <span className="hidden sm:inline text-muted-foreground dark:text-[#8a8f98]" style={navText}>
              {admin.email}
            </span>
          )}
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-white/[0.04] transition-colors"
            style={navText}
          >
            <SignOut size={15} />
            Sign out
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav className="w-full sm:w-52 shrink-0 border-b sm:border-b-0 sm:border-r border-border/50 dark:border-white/[0.06] px-3 py-4">
          <p className={cn(sectionEyebrowClass, 'px-2 pb-2')} style={sectionEyebrowStyle}>
            Report &amp; feedback
          </p>
          <ul className="flex sm:flex-col gap-0.5">
            {NAV.map(({ to, label, icon: Icon }) => (
              <li key={to} className="flex-1 sm:flex-none">
                <NavLink
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-2 h-8 transition-colors duration-150',
                      isActive
                        ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]',
                    )
                  }
                  style={navText}
                >
                  <Icon size={16} />
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <main className="flex-1 min-w-0 px-4 py-5 sm:px-8 sm:py-7">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
